/* eslint-disable camelcase */
import {
  useState,
  useEffect,
  useRef,
  useMemo
} from 'react';
import { format } from 'date-fns';
import { useStreamData } from '@lib/hooks/useStreamData';
import {
  stopEngagement,
  postIpaUserEvent,
  wakeUpLambdaFunctions,
  connectToYoutubeStream,
  getLinkQuestions
} from '@services/youtube-platform.service';
import ZoomSandbox from '@components/Sandbox/Zoom';
import Image from '@atoms/Image';
import { useRouter } from 'next/router';
import Button from '@atoms/new/button';
import FEATURES, { getByIndex, getFeatureBySlug } from '@components/Sandbox/features';
import TwitchSandbox from '@components/Sandbox/Twitch';
import IpaSandbox from '@components/Sandbox/Ipa';
import useDeviceOrientation from '@lib/hooks/useDeviceOrientation';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import RequestLandscapeModal from '@components/Sandbox/RequestLandscapeModal';
import { pushDataLayerForEvent } from '@lib/gtag';
import YoutubeSandbox from '@components/Sandbox/Youtube';
import MeetSandbox from '@components/Sandbox/Meet';
import NoticeBubble from '@atoms/NoticeBubble';
import TeamsSandbox from '@components/Sandbox/Teams';
import { PlatformDropdown } from '@components/Sandbox/PlatformDropdown';
import { PLATFORMS } from '@components/sessions/platforms';
import { FeaturesDropdown } from '@components/Sandbox/FeaturesDropdown';
import { SignUpModal } from './SignupModal';
import { useDisclosure } from '@heroui/react';
import { nonInteractiveFeatures } from '@components/Layout/helper';
import KickSandbox from './kick';
import RestreamSandbox from './Restream';
import StreamyardSandbox from './Streamyard';
import AirmeetSandbox from './Airmeet';
import LinkedInSandbox from './linkedin';
import { useGeneratedResponses } from './useGeneratedResponses';
import Loader from '@atoms/Atom/Loader';

export default function SandboxV1({
  platformId,
  platform = 'zoom',
  isGuest = false,
  is_ros_preview = false,
  interaction = 'slide-first',
  templateId = 'bb65b7fa-48b5-46d5-84dd-4271cd9dc459'

}) {
  const router = useRouter();
  const { isMobile, orientation } = useDeviceOrientation();

  const [feature, setFeature] = useState(getFeatureBySlug(interaction));

  const [linkQuestions, setLinkQuestions] = useState(null);
  const [currentRosFeature, setCurrentRosFeature] = useState();
  const [next] = useState(linkQuestions?.length === 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFeatureId] = useState(null);

  const [crudLoading, setCrudLoading] = useState(true);
  const teamContext = useTeamsData((s) => s.teamContext);
  const { setIntervalId } = useStreamData(s => ({ setIntervalId: s.setIntervalId }));
  const [currentRosResult] = useState([]);
  const [stream, setStream] = useState(null);
  const [notificationVisible, setNotificationVisible] = useState(isMobile);

  useGeneratedResponses({
    feature: currentRosFeature,
    streamId: stream?.id,
    streamTitle: stream?.title === 'sandbox' ? 'Engaging LiveStream' : stream?.title,
  });

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const {
    isOpen: isOpenSignup,
    onOpen: onOpenSignup,
    onOpenChange: onOpenSignupChange,
    onClose: onCloseSignup
  } = useDisclosure({
    onOpen: () => {
      signUpShownRef.current = true;
    }
  });

  useEffect(() => {
    if (!isGuest && isOpenSignup) {
      onCloseSignup();
    }
  }, [isGuest]);

  const nextPressCountRef = useRef(0);
  const prevPressCountRef = useRef(0);
  const signUpShownRef = useRef(false);

  const featurePrevState = () => {
    if (currentIndex > 0) {
      if (isGuest && prevPressCountRef.current === 0 && !['PDF Slide'].includes(currentRosFeature.type) && !signUpShownRef.current) {
        prevPressCountRef.current += 1;
        onOpenSignup();
        return;
      }

      pushDataLayerForEvent('sandbox_previous_arrow_pressed', { streamId: stream.id }, false);
      setSelectedFeatureKeys(new Set([getByIndex(currentIndex - 1).slug]));
    }
  };

  const featureNextState = () => {
    if (currentIndex < linkQuestions.length - 1) {
      if (isGuest && nextPressCountRef.current === 0 && !['PDF Slide'].includes(currentRosFeature.type) && !signUpShownRef.current) {
        nextPressCountRef.current += 1;
        onOpenSignup();
        return;
      }

      pushDataLayerForEvent('sandbox_next_arrow_pressed', { streamId: stream.id }, false);
      setSelectedFeatureKeys(new Set([getByIndex(currentIndex + 1).slug]));
    }
  };

  const stopFeature = async (id, type) => {
    if (nonInteractiveFeatures.includes(currentRosFeature.type)) {
      return;
    }

    pushDataLayerForEvent(`sandbox_stop_${currentRosFeature.type}_interaction`, { streamId: stream.id });

    const sessionId = localStorage.getItem('sessionId');
    stopEngagement(
      stream.id,
      type,
      {
        sessionId,
        engagementId: id,
        platformType: stream.platform_type
      }
    );
  };

  useEffect(() => {
    if (currentIndex === undefined || currentIndex === null || !linkQuestions) return;
    stopFeature(currentFeatureId, currentRosFeature?.type);
    setCurrentRosFeature(linkQuestions[currentIndex]);
    pushDataLayerForEvent(`sandbox_start_${currentRosFeature.type}_interaction`, { streamId: stream.id }, false);
  }, [currentIndex, linkQuestions]);

  const connectToStream = async (event) => {
    await wakeUpLambdaFunctions();
    const newIntervalId = setInterval(async () => {
      await wakeUpLambdaFunctions();
    }, 15 * 60 * 1000);
    setIntervalId(newIntervalId);
    const scheduledStartTime = event.event_time;
    const engageType = new Date().getTime() > new Date(scheduledStartTime).getTime()
      ? 'active'
      : 'scheduled';

    const res = await connectToYoutubeStream({
      liveChatId: event.id,
      platformId: event.platform_id,
      statusFlag: true,
      engageType,
      platformType: event.livestream.platform_type,
      entityId: event.id,
      title: event.title,
      userId: event.livestream.user_id,
      orgStartTime: event.event_time,
      totalTimeWithText: '12 PM'
    });

    setStream(res.entity);

    const id = res.entity.template_id;
    const tempQuestions = await getLinkQuestions(id);
    setLinkQuestions(tempQuestions.entity);

    setCurrentRosFeature(tempQuestions.entity[currentIndex]);

    localStorage.setItem('streamId', res.entity.id);
    localStorage.setItem('sessionId', res.entity.sessionId);
    window.parent.postMessage('ready', '*');
  };

  const connectSandbox = async () => {
    setCrudLoading(true);
    const date = new Date();
    const event_time = date.toISOString();

    const res = await postIpaUserEvent({
      platform_id: platformId,
      title: 'sandbox',
      is_sandbox: true,
      is_ros_preview,
      event_time,
      user_id: teamContext?.oid,
      template_id: templateId,
      event_type: 'ipa',
      totalTimeWithText: format(new Date(event_time), 'hh:mm a')
    });

    connectToStream(res.entity);

    setCrudLoading(false);
  };

  useEffect(() => {
    connectSandbox();
  }, []);

  const handleKeyStrokes = (e) => {
    const { key } = e;
    if (!key) return;

    if (key.match(/ArrowRight/)) {
      e.preventDefault();
      featureNextState();
    }

    if (key.match(/ArrowLeft/)) {
      e.preventDefault();
      featurePrevState();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyStrokes);
    return () => {
      window.removeEventListener('keydown', handleKeyStrokes);
    };
  }, [currentIndex, currentRosFeature]);

  const [showOrientationModal, setShowOrientationModal] = useState(false);

  useEffect(() => {
    setNotificationVisible(isMobile);

    if (isMobile && orientation === 'Portrait') {
      setShowOrientationModal(true);
      return;
    }

    setShowOrientationModal(false);
  }, [isMobile, orientation]);

  const [selectedKeys, setSelectedKeys] = useState(new Set([platform]));

  const [platformDetails, setPlatformDetails] = useState();
  const platforms = Object.values(PLATFORMS).sort((a, b) => a.order - b.order);

  const selectedValue = useMemo(
    () => Array.from(selectedKeys).join(', ').replaceAll('_', ' '),
    [selectedKeys]
  );

  useEffect(() => {
    if (!isGuest) return;
    if (!currentRosFeature) return;

    nextPressCountRef.current = 0;
    prevPressCountRef.current = 0;
    signUpShownRef.current = false;
  }, [isGuest, currentRosFeature]);

  useEffect(() => {
    let platform = platforms.find((p) => p.slug === selectedValue);

    if (!platform) return;

    setPlatformDetails(platform);
    setPlatform(platform.slug);
  }, [selectedValue]);

  const [selectedFeatureKeys, setSelectedFeatureKeys] = useState(new Set([interaction]));

  const selectedFeatureValue = useMemo(
    () => Array.from(selectedFeatureKeys).join(', ').replaceAll('_', ' '),
    [selectedFeatureKeys]
  );

  useEffect(() => {
    let feature = FEATURES.find((f) => f.slug === selectedFeatureValue);

    if (!feature) return;
    setFeature(feature);
    setFeatureURL(feature.slug);
    setCurrentIndex(feature.index);
  }, [selectedFeatureValue]);

  const getPlatformUI = () => {
    if (platform === 'kick') {
      return <KickSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'linkedin') {
      return <LinkedInSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'restream') {
      return <RestreamSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'streamyard') {
      return <StreamyardSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'airmeet') {
      return <AirmeetSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'teams') {
      return <TeamsSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'ipa') {
      return <IpaSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'twitch') {
      return <TwitchSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'meet') {
      return <MeetSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'youtube') {
      return <YoutubeSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    return <ZoomSandbox
      stream={stream}
      currentIndex={currentIndex}
      featurePrevState={featurePrevState}
      featureNextState={featureNextState}
      linkQuestions={linkQuestions || []}
      nextRef={nextRef}
      prevRef={prevRef}
      next={next}
      feature={currentRosFeature}
      stopFeature={stopFeature}
      currentRosResult={currentRosResult}
    />;
  };

  const setPlatform = (newPlatform) => {
    pushDataLayerForEvent('sandbox_platform_changed', { platform: newPlatform });
    router.push(`${router.route}?platform=${newPlatform}&interaction=${feature.slug}`);
  };

  const setFeatureURL = (featureSlug) => {
    pushDataLayerForEvent('sandbox_feature_changed', { feature: featureSlug });
    router.push(`${router.route}?platform=${platform}&interaction=${featureSlug}`);
  };

  if (crudLoading || !stream || !linkQuestions?.length) return <Loader />;

  return (
    <div className='relative h-full'>
      <div className='flex items-center justify-between bg-white dark:bg-grey-900 z-50 py-3 px-4 border-b border-grey-200 dark:border-grey-800'>
        <a href='https://streamalive.com' target='_blank'>
          <Image tabIndex={2} width={30} height={30}
            src='/images/logos/logo_tag_black.svg'
            className=" lg:w-47.5 md:w-40 w-24 md:block cursor-pointer" alt="Back Arrow" />
        </a>
        <div className='flex items-center'>
          <FeaturesDropdown
            featureDetails={feature}
            feature={selectedFeatureKeys}
            setFeature={setSelectedFeatureKeys}
          />
          <p className='mx-4'>IN</p>
          <PlatformDropdown
            selectedKeys={selectedKeys}
            platformDetails={platformDetails}
            setSelectedKeys={setSelectedKeys}
          />
        </div>
        {isGuest ? (
          <Button
            onClick={() => window.open(process.env.NEXT_PUBLIC_REGISTER_ROUTE, '_blank')}
          >
            Sign up, it’s free
          </Button>
        ) : <div className='w-20' />}
      </div>
      <div>
        <div
          className='p-4 max-w-[1280px] min-w-[480px] mx-auto'>
          {getPlatformUI()}
        </div>
        <p className='text-center text-grey-700 text-sm font-normal mt-2'>This preview lets you interact with both screens to see how it works for the presenter and your audience in real-time.</p>
      </div>

      <RequestLandscapeModal visible={showOrientationModal} />
      <NoticeBubble
        visible={notificationVisible}
        onClose={() => setNotificationVisible(false)}
        message="This demo is best seen in a larger screen."
      />

      {isOpenSignup && (
        <SignUpModal
          isOpen={isOpenSignup}
          onOpenChange={onOpenSignupChange}
          onClose={() => {
            if (nextPressCountRef.current === 1) featureNextState();
            if (prevPressCountRef.current === 1) featurePrevState();

            onCloseSignup();
          }}
        />
      )}
    </div>
  );
}
